import { useState, useEffect, MouseEvent, ReactNode, KeyboardEvent, Children, isValidElement, cloneElement } from "react";
import { icons, translations } from "../../Helpers/WindowHelpers";
import parse from 'html-react-parser';
import { createPortal } from "react-dom";
interface ModalInterface {
    classes?: string,
    bodyClasses?: string,
    buttonBody: string,
    children: ReactNode,
}

const Modal = ({ classes, bodyClasses, buttonBody, children }: ModalInterface) => {
    const [isOpen, setIsOpen] = useState(false);

    const closeModal = () => setIsOpen(false);
    const openModal = () => setIsOpen(true);

    useEffect(() => {
        if (!isOpen) {
            document.body.classList.remove('modal-open');
            return;
        }

        document.body.classList.add('modal-open');
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [isOpen]);

    return (
        <>
            <button
                tabIndex={0}
                role="button"
                type="button"
                onClick={openModal}
                className={`modal-trigger ${classes || ''}`}>{parse(buttonBody ?? '')}</button>
            {isOpen && typeof document !== 'undefined' && createPortal(
                <ModalBody
                    isOpen={isOpen}
                    closeModal={closeModal}
                    classes={bodyClasses}>
                    {children}
                </ModalBody>,
                document.body
            )}
        </>
    );
}

interface ModalBodyInterface {
    classes?: string,
    isOpen: boolean,
    closeModal: () => void,
    children: ReactNode
}

const ModalBody = ({ classes, isOpen, closeModal, children }: ModalBodyInterface) => {
    if (!isOpen) {
        document.body.classList.remove('modal-open');
        return false;
    } else {
        document.body.classList.add('modal-open');
    }
    return (
        <div className="modal"
            // Close when you click on the greyed out area
            onClick={(event: MouseEvent<HTMLDivElement>) => {
                if (event.target !== event.currentTarget) return;
                closeModal()
            }}
            onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                //@ts-ignore
                const focusableElements = tcgFocusableElements(event.currentTarget);

                if (event.key === 'Tab') {
                    if (event.shiftKey) { // shift + tab back
                        if (document.activeElement == focusableElements[0] || !focusableElements.includes(document.activeElement)) {
                            focusableElements[focusableElements.length - 1].focus();
                            event.preventDefault();
                        }
                    } else { // tab forward
                        if (document.activeElement == focusableElements[focusableElements.length - 1] || !focusableElements.includes(document.activeElement)) {
                            focusableElements[0].focus();
                            event.preventDefault();
                        }
                    }
                }
            }}
        >
            <div className={`modal-content round ${classes}`}>
                <button
                    className="modal-close-button js-close bg-color--black color--white line-height-0 round round--lg"
                    aria-label={translations.accessibility.close}
                    onClick={closeModal}>
                    {parse(icons.close ?? '')}
                </button>
                {Children.map(children, (child) => {
                    if (isValidElement(child)) {
                        return cloneElement(child, { closeModal: closeModal } as any);
                    }
                    return child;
                })}
            </div>
        </div>
    );
}

export default Modal;